// @ts-check
/* global module require */
'use_strict';

import { YOTPOBadge } from './yotpo_badge';
import { config } from '../tv_utils/global_config_data.js';

// The Yotpo class is defined in a third party script, so we can't import it here.
// Instead, we're adding Yotpo to the Window interface to make TS happy.
declare global {
    interface Window {
        Yotpo: any;
        yotpo: any;
        tv_yotpo_loaded?: boolean;
    }
}

/**
 * Class to manage the UI for the YOTPO Site Reviews.
 */
export class SiteReviewsView {

    /**
     * @var {MutationObserver} observer to watch when the Site Reviews Element loads.
     */
    observer: MutationObserver;

    /**
     * @param {HTMLDivElement} element is the Site Reviews element.
     */
    constructor(element: Element) {
        // Init the observer with the function to run on the mutations it observes.
        this.observer = new MutationObserver(this.observe.bind(this));

        // Tell the observer to observe the element and children.
        this.observer.observe(element, { childList: true });
    }

    /**
     * Callback for the mutation observer to run when it observes a mutation.
     *
     * @returns {void}
     */
    observe() {
        const site_review_tab = <HTMLElement> document.querySelector(
            '#yotpo-testimonials-custom-tab [data-type="site"]'
        );

        // We want to activate the Site Reviews tab if it isn't already.
        if (!site_review_tab.classList.contains('yotpo-active')) {
            // YOTPO scripts add a click event to the tab selector.
            site_review_tab.click();
        }

        this.observer.disconnect();
    }
}

/**
 * Function to handle scrolling back to the reviews after the reviews page is changed.
 *
 * @param {Element} element is the parent element the reviews are in to watch and scroll to.
 *
 * @return {void}
 */
function scroll_pagination(element: Element) : void {
    const pagination_observer = new MutationObserver(() => {
        // Find if there are Review Pages.
        const pages = element.querySelector('.yotpo-pager');

        // If there are reviews or questions, hide he appropriate buttons.
        const write_review_btn = document.querySelector('.write-question-review-buttons-container .write-review-button');
        const write_question_btn = document.querySelector('.write-question-review-buttons-container .write-question-button');
        const reviews_amount = document.querySelector('.reviews-amount');
        if (!reviews_amount && write_review_btn) {
            write_review_btn.classList.add('tv-display__none');
        }

        const yotpo_questions = document.querySelector('.yotpo-first-review');
        if (yotpo_questions && write_question_btn) {
            write_question_btn.classList.add('tv-display__none');
        }

        if (pages) {
            // For each page anchor, have it scroll the review section into view.
            for (const page of pages.querySelectorAll('a')) {
                page.addEventListener('click', () => {
                    setTimeout(() => {
                        element.scrollIntoView(true);
                    }, 125); // Wait for Yotpo to finish.
                    return false;
                });
            }
        } else {
            pagination_observer.disconnect();
        }
    });
    pagination_observer.observe(element, { childList: true, subtree: true });
    // Check if we are allowing guests to write reviews.
    const write_review_enable = config().yotpo_write_review;
    if (write_review_enable) {
        element.classList.remove('tv-guest');
    }
}

/**
 * Looks for YOTPO specific element to run custom script view.
 *
 * @returns {void}
 */
export function init() {
    const site_reviews = document.getElementById('yotpo-testimonials-custom-tab');
    if (site_reviews) {
        scroll_pagination(site_reviews);
        (() => new SiteReviewsView(site_reviews))();
    }

    const product_reviews = document.querySelector('.tv-yotpo__reviews');
    if (product_reviews) {
        scroll_pagination(product_reviews);
    }

    const header_badge = document.querySelector('.tv-header-badge .yotpo-badge');
    if (header_badge) {
        const badge_observer = new MutationObserver(() => {
            (() => new YOTPOBadge(<HTMLDivElement> header_badge))();
            badge_observer.disconnect();
        });
        badge_observer.observe(header_badge, { childList: true });
    }
}